import { useQuery } from "@apollo/client";
import {
  AnnotationIcon,
  CalendarIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  StarIcon,
  TrashIcon,
} from "@heroicons/react/outline";
import { isNil, uniq } from "lodash";
import moment from "moment";
import { useCallback, useState } from "react";
import {
  ArtifactType,
  CreatedArtifactFragmentFragment,
  FeedbackState,
  GetProfileCoachingInsightsQueryQuery,
  GetProfileCoachingInsightsQueryQueryVariables,
  ManagerInsightType,
  ProfileCoachingInsightFragmentFragment,
} from "types/graphql-schema";
import { BasicUser } from "types/topicflow";

import AddTopicToMeetingDialog from "@apps/add-topic-to-meeting-dialog/add-topic-to-meeting-dialog";
import ArtifactCreationDialog from "@apps/artifact-creation-dialog/artifact-creation-dialog";
import useLabel from "@apps/use-label/use-label";
import useUiPreferenceCache from "@apps/use-ui-preference-cache/use-ui-preference-cache";
import { currentUserVar } from "@cache/cache";
import Button, { buttonTheme } from "@components/button/button";
import Dropdown from "@components/dropdown/dropdown";
import Loading from "@components/loading/loading";
import Modal from "@components/modal/modal";
import ModalTitle from "@components/modal/modal-title";
import CustomTextareaAutosize from "@components/textarea-autosize/textarea-autosize";
import { TopicflowIcon } from "@components/topicflow-icon/topicflow-icon";

import ProfileEmptyState from "../../profile-empty-state";
import getProfileArtifactsQuery from "../graphql/get-profile-artifacts-query";
import getProfileCoachingInsightsQuery from "../graphql/get-profile-coaching-insights-query";

const CoachingInsight = ({
  insight,
  profile,
  onDismiss,
}: {
  insight: ProfileCoachingInsightFragmentFragment;
  profile: BasicUser;
  onDismiss: (insightTitle: string) => void;
}) => {
  const [dismissOtherReason, setDismissOtherReason] = useState("");
  const [showDismissOtherReasonDialog, setShowDismissOtherReasonDialog] =
    useState(false);
  const [showActionDialog, setShowActionDialog] = useState(false);
  const currentUser = currentUserVar();
  const label = useLabel();
  const Icon =
    insight.insightType === ManagerInsightType.Feedback
      ? AnnotationIcon
      : insight.insightType === ManagerInsightType.DiscussionTopic
      ? CalendarIcon
      : StarIcon;
  const buttonText =
    insight.insightType === ManagerInsightType.Feedback
      ? `Request ${label("feedback")}`
      : insight.insightType === ManagerInsightType.DiscussionTopic
      ? `Add to ${label("1-on-1", { pluralize: true })}`
      : `Give ${label("recognition")}`;

  const handleClickAction = () => {
    setShowActionDialog(true);
  };

  const handleCloseAction = () => {
    setShowActionDialog(false);
  };

  const handleCreateArtifact = (
    artifact: CreatedArtifactFragmentFragment | undefined
  ) => {
    if (artifact) {
      handleClickDismiss();
    }
    setShowActionDialog(false);
  };

  const handleCreatedMeetingTopic = () => {
    handleClickDismiss();
    setShowActionDialog(false);
  };
  const handleClickDismiss = () => {
    setShowDismissOtherReasonDialog(false);
    onDismiss(insight.title);
  };

  return (
    <div className="flex gap-3 p-6">
      <div className="w-4 shrink-0 mt-0.5 text-blue-link">
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 flex flex-col gap-1">
        <div className="text-gray-900">{insight.title}</div>
        <div className="text-gray-600">{insight.description}</div>
        <div className="mt-2 flex items-center gap-4">
          <Button
            text={buttonText}
            theme={buttonTheme.primary}
            onClick={handleClickAction}
          />
          <Dropdown
            options={[
              {
                label: "I've already handled this insight externally",
                onClick: handleClickDismiss,
              },
              {
                label: "The insight was irrelevant",
                onClick: handleClickDismiss,
              },
              {
                label: "The insight was inaccurate or incorrect",
                onClick: handleClickDismiss,
              },
              {
                label: "I don’t want insights related to this topic",
                onClick: handleClickDismiss,
              },
              {
                label: "Other reason",
                onClick: () => setShowDismissOtherReasonDialog(true),
              },
            ]}
          >
            <Button text="Dismiss" />
          </Dropdown>

          {showDismissOtherReasonDialog && (
            <Modal
              alignedTop
              onClose={() => setShowDismissOtherReasonDialog(false)}
            >
              <div className="flex flex-col gap-6 p-6">
                <ModalTitle
                  onClose={() => setShowDismissOtherReasonDialog(false)}
                >
                  Other Reason
                </ModalTitle>
                <div>
                  <CustomTextareaAutosize
                    value={dismissOtherReason}
                    onChange={(e) => setDismissOtherReason(e.target.value)}
                    placeholder="Write your reason here..."
                    className="w-full text-sm border rounded-md px-2 py-1"
                  />
                </div>
                <div className="flex items-center gap-4 justify-end">
                  <Button
                    text="Cancel"
                    onClick={() => setShowDismissOtherReasonDialog(false)}
                  />
                  <Button
                    text="Dismiss this insight"
                    theme={buttonTheme.primary}
                    onClick={handleClickDismiss}
                    disabled={dismissOtherReason.trim().length === 0}
                  />
                </div>
              </div>
            </Modal>
          )}
          {showActionDialog &&
            insight.insightType === ManagerInsightType.Feedback && (
              <ArtifactCreationDialog
                formOptions={{
                  artifactType: ArtifactType.Feedback,
                  feedbackRecipients: [profile],
                  feedbackSenders: [],
                  feedbackState: FeedbackState.Requested,
                  feedbackRecipientsManagersCanView: true,
                  feedbackRecipientsCanView: false,
                  feedbackCreatorCanView: true,
                  feedbackAdminsCanView: false,
                  title: insight.suggestedArtifactTitle || insight.title,
                }}
                refetchQueries={[getProfileArtifactsQuery]}
                onClose={handleCreateArtifact}
              />
            )}
          {showActionDialog &&
            insight.insightType === ManagerInsightType.Recognition && (
              <ArtifactCreationDialog
                formOptions={{
                  artifactType: ArtifactType.Recognition,
                  recognitionRecipients: [profile],
                  recognitionCoreValueId: insight.suggestedCoreValue?.id,
                  title: insight.suggestedArtifactTitle || insight.title,
                }}
                refetchQueries={[getProfileArtifactsQuery]}
                onClose={handleCreateArtifact}
              />
            )}
          {showActionDialog &&
            insight.insightType === ManagerInsightType.DiscussionTopic && (
              <AddTopicToMeetingDialog
                variables={{
                  startDatetimeAfter: moment().startOf("hour").format(),
                  participants: [currentUser.id, profile.id],
                  isFormalOneonone: true,
                }}
                topicTitle={insight.title}
                onClose={handleCloseAction}
                onCreatedTopic={handleCreatedMeetingTopic}
              />
            )}
        </div>
      </div>
    </div>
  );
};

const ProfileCoachingInsights = ({ profile }: { profile: BasicUser }) => {
  const currentUser = currentUserVar();
  const isReport = currentUser?.directReports.edges.some(
    (report) => report?.node?.id === profile.id
  );
  const [dismissedInsights, setDismissedInsights] = useState<string[]>([]);
  const { uiPreferenceCache, saveUiPreference } = useUiPreferenceCache();

  const { data, loading } = useQuery<
    GetProfileCoachingInsightsQueryQuery,
    GetProfileCoachingInsightsQueryQueryVariables
  >(getProfileCoachingInsightsQuery, {
    variables: { profileId: profile.id },
    skip: !isReport,
  });

  const handleClickToggleCoachingInsights = useCallback(() => {
    const coachingInsightsExpanded =
      !uiPreferenceCache.coachingInsightsExpanded;
    saveUiPreference({ coachingInsightsExpanded });
  }, [uiPreferenceCache, saveUiPreference]);

  if (!isReport) {
    return null;
  }

  const handleDismissInsight = (insightTitle: string) => {
    setDismissedInsights(uniq([...dismissedInsights, insightTitle]));
  };

  const insights = (data?.user?.managerInsights?.edges || [])
    .map((edge) => edge?.node)
    .filter((insight) => !isNil(insight));
  const insightsCount = insights.length - dismissedInsights.length;

  return (
    <div className="flex flex-col border rounded-lg text-sm border-purple-200 overflow-hidden">
      <div className="flex items-center justify-between px-6 py-4 ">
        <div className="flex-1 font-medium text-base text-gray-900 flex items-center gap-2">
          Coaching Insights
          <div>
            <span className="text-sm text-purple-900 bg-purple-100 px-1.5 py-px flex items-center gap-1 rounded-md">
              <TopicflowIcon type="ai" className="w-3 h-3" />
              {insightsCount}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            icon={
              uiPreferenceCache.coachingInsightsExpanded
                ? ChevronUpIcon
                : ChevronRightIcon
            }
            theme={buttonTheme.iconGray}
            onClick={handleClickToggleCoachingInsights}
          />
        </div>
      </div>
      {uiPreferenceCache.coachingInsightsExpanded && (
        <div className="bg-white">
          {loading && !data ? (
            <Loading className="" />
          ) : insights.length > 0 ? (
            <div className="flex flex-col divide-y">
              {insights.map((insight) =>
                dismissedInsights.includes(insight.title) ? (
                  <div key={insight.title}>
                    <div className="flex items-center gap-3 px-6 py-4 text-gray-500">
                      <TrashIcon className="w-4 h-4" />
                      Insight Dismissed
                    </div>
                  </div>
                ) : (
                  <CoachingInsight
                    key={insight.title}
                    insight={insight}
                    profile={profile}
                    onDismiss={handleDismissInsight}
                  />
                )
              )}
            </div>
          ) : (
            <ProfileEmptyState>No coaching insights.</ProfileEmptyState>
          )}
        </div>
      )}
    </div>
  );
};

export default ProfileCoachingInsights;
